import React, { useState, useEffect } from "react";
import { Layout, Form, Input, Button, Typography, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  LoadingOutlined,
} from "@ant-design/icons";
import "../styles/login.scss";
import { appEnvType } from "../utils/consts";
import MessageProvider from "../utils/MessageProvider";
import {
  getStoredEmbedData,
  isTokenExpired,
  cacheEmbedData,
  clearExpiredFlag,
  hasExpiredFlag,
} from "../utils/session";

const { Sider, Content } = Layout;
const { Title } = Typography;

// Embed data interface (reuse shape from session util comments)
interface EmbedDataShape {
  token?: string;
  embedUrl?: string;
  reportId?: string;
  [k: string]: any;
}

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Login";
    try {
      const cached = getStoredEmbedData();
      if (cached && !isTokenExpired(cached)) {
        if (hasExpiredFlag()) clearExpiredFlag();
        navigate("/dashboard", { replace: true, state: { apiData: cached } });
      }
    } catch {}
  }, [navigate]);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (process.env.REACT_APP_MODE !== appEnvType.development) {
        const key = process.env.REACT_APP_APIM_SUBSCRIPTION_KEY || "";
        if (key) headers["Ocp-Apim-Subscription-Key"] = key;
      }
      const res = await fetch(
        `${process.env.REACT_APP_PWA_API}/webhook-request/generate-embeded-token?api-version=1`,
        { method: "POST", headers, body: JSON.stringify(values) },
      );
      let payload: any = null;
      try {
        payload = await res.json();
      } catch {}
      if (!res.ok) {
        const apiMsg = payload?.message?.toString()?.trim();
        throw new Error(apiMsg || `Request failed (${res.status})`);
      }
      const embedData: EmbedDataShape = (payload?.result as any) || {};
      cacheEmbedData(embedData);
      clearExpiredFlag();
      navigate("/dashboard", { replace: true, state: { apiData: embedData } });
    } catch (err: any) {
      console.log({ err });
      MessageProvider.errorMessage(err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className="login-layout">
      {loading && (
        <div
          className="global-loading-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Spin size="large" indicator={<LoadingOutlined spin />} />
        </div>
      )}
      <Sider className="login-sider">
        <div className="login-sider-inner">
          <div className="login-sider-content">
            <h1 className="welcome">Welcome to</h1>
            <img
              src="/connecthq-logo-white.svg"
              alt="Connect HQ Logo"
              className="login-logo"
            />
          </div>
        </div>
      </Sider>
      <Content className="login-content-wrapper">
        <div className="login-card">
          <div className="login-title">
            <Title level={2}>Login</Title>
          </div>
          <Form
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
            className="login-form"
          >
            <Form.Item
              label={
                <>
                  <span>Email</span>
                  <pre className="req-mark">*</pre>
                </>
              }
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email" },
              ]}
            >
              <Input
                className="login-input"
                placeholder="Enter Email"
                autoComplete="email"
                size="large"
              />
            </Form.Item>
            <Form.Item
              label={
                <>
                  <span>Password</span>
                  <pre className="req-mark">*</pre>
                </>
              }
              name="password"
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
            >
              <Input.Password
                className="login-input"
                placeholder="Enter Password"
                autoComplete="current-password"
                size="large"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Login
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Content>
    </Layout>
  );
};

export default LoginPage;
